import {
  DateFormatter,
  fromDate,
  getLocalTimeZone,
  now
} from "@internationalized/date";
import { getSupportedUserLocale } from "src/lib/i18next";
import translate from "src/lib/translate";
import type { LeapChatMessage } from "./types";

const formatRelativeMs = (epochMs: number): string | null => {
  const diffMs = Date.now() - epochMs;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) {
    return translate("leapChat.time.justNow");
  }
  if (diffMin < 60) {
    return translate("leapChat.time.minutesAgo", { count: diffMin });
  }
  if (diffHr < 24) {
    return translate("leapChat.time.hoursAgo", { count: diffHr });
  }
  if (diffDays === 1) {
    return translate("leapChat.time.yesterday");
  }
  if (diffDays < 7) {
    return translate("leapChat.time.daysAgo", { count: diffDays });
  }

  return null;
};

export const formatMessageTime = (epochMs: number): string => {
  const relative = formatRelativeMs(epochMs);
  if (relative) {
    return relative;
  }

  const tz = getLocalTimeZone();
  const zdt = fromDate(new Date(epochMs), tz);
  const currentYear = now(tz).year;
  return new DateFormatter(getSupportedUserLocale(), {
    month: "short",
    day: "numeric",
    ...(zdt.year !== currentYear ? { year: "numeric" } : {})
  }).format(zdt.toDate());
};

export const formatRelativeTime = (dateStr: string): string => {
  const epochMs = new Date(dateStr).getTime();
  const relative = formatRelativeMs(epochMs);
  if (relative) {
    return relative;
  }

  const diffWeeks = Math.floor((Date.now() - epochMs) / 604_800_000);
  if (diffWeeks === 1) {
    return translate("leapChat.time.weeksAgo", { count: 1 });
  }
  return translate("leapChat.time.weeksAgoPlural", { count: diffWeeks });
};

export const formatConversationDate = (dateStr: string): string => {
  const tz = getLocalTimeZone();
  const zdt = fromDate(new Date(dateStr), tz);
  const epochMs = zdt.toDate().getTime();

  const time = new DateFormatter(getSupportedUserLocale(), {
    hour: "numeric",
    minute: "2-digit"
  }).format(zdt.toDate());

  const diffDays = Math.floor((Date.now() - epochMs) / 86_400_000);
  if (diffDays === 0) {
    return translate("leapChat.time.todayAt", { time });
  }
  if (diffDays === 1) {
    return translate("leapChat.time.yesterdayAt", { time });
  }
  if (diffDays < 7) {
    return translate("leapChat.time.daysAgoAt", {
      count: diffDays,
      time
    });
  }

  const dateFormatted = new DateFormatter(getSupportedUserLocale(), {}).format(
    zdt.toDate()
  );
  return `${dateFormatted}, ${time}`;
};

export const normalizeMessageText = (text: string): string =>
  text
    .replace(/[^\S\n]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const isEmptyNonLocalMessage = (msg: LeapChatMessage): boolean => {
  if (msg.isLocal || msg.localId) {
    return false;
  }
  if (msg.text.trim()) {
    return false;
  }
  if (msg.toolCalls?.length) {
    return false;
  }
  if (msg.files?.length) {
    return false;
  }
  if (msg.senderName || msg.senderDescription) {
    return false;
  }
  return true;
};

export const isEmptyMessage = (msg: LeapChatMessage): boolean => {
  if (msg.error) {
    return false;
  }
  if (msg.text.trim()) {
    return false;
  }
  if (msg.toolCalls?.length) {
    return false;
  }
  if (msg.files?.length) {
    return false;
  }
  if (msg.senderName || msg.senderDescription) {
    return false;
  }
  return true;
};
